(() => {
  const SELECTOR = '[data-custom-purchase-options-v2]';

  function parseJSON(element, selector) {
    try {
      return JSON.parse(element.querySelector(selector)?.textContent || '{}');
    } catch (error) {
      console.warn('RHYA purchase options: invalid JSON.', error);
      return {};
    }
  }

  function toNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function readNumber(value) {
    const match = String(value || '').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : null;
  }

  function planMetadata(allocation) {
    const meta = {
      quantity: null,
      frequency: null,
      unit: '',
    };

    (allocation.options || []).forEach((option) => {
      const name = normalize(option.name);
      const value = String(option.value || '');

      if (name.includes('product quantity') || name === 'quantity') {
        meta.quantity = readNumber(value);
      }

      if (name.includes('order frequency') || name.includes('delivery frequency')) {
        meta.frequency = readNumber(value);
      }

      if (name === 'unit' || name.includes('frequency unit') || name.includes('order interval unit')) {
        meta.unit = normalize(value);
      }
    });

    const planName = normalize(allocation.planName);

    if (!meta.frequency) {
      const monthMatch = planName.match(/(\d+)\s*month/);
      if (monthMatch) meta.frequency = Number(monthMatch[1]);
      else if (planName.includes('monthly')) meta.frequency = 1;
    }

    if (!meta.quantity) {
      if (planName.includes('3 month') || planName.includes('three month')) meta.quantity = 3;
      else meta.quantity = 1;
    }

    if (!meta.unit) meta.unit = 'month';
    if (!meta.frequency) meta.frequency = 1;

    return meta;
  }

  function buildOffers(variant) {
    const oneTimePrice = toNumber(variant.price);
    const oneTimeCompare = Math.max(toNumber(variant.compareAtPrice), oneTimePrice);

    const offers = {
      onetime: {
        key: 'onetime',
        quantity: 1,
        planId: '',
        totalPrice: oneTimePrice,
        totalCompare: oneTimeCompare,
        frequency: 1,
        unit: 'month',
        discountPercent: oneTimeCompare > oneTimePrice
          ? Math.round((1 - oneTimePrice / oneTimeCompare) * 100)
          : 0,
      },
    };

    (variant.allocations || []).forEach((allocation) => {
      const meta = planMetadata(allocation);
      const quantity = Math.max(1, toNumber(meta.quantity, 1));
      const frequency = Math.max(1, toNumber(meta.frequency, 1));
      const unitPrice = toNumber(allocation.price);
      const normalUnitPrice = Math.max(
        toNumber(allocation.compareAtPrice),
        oneTimePrice
      );
      const totalPrice = unitPrice * quantity;
      const totalCompare = normalUnitPrice * quantity;
      const discountPercent = totalCompare > totalPrice
        ? Math.round((1 - totalPrice / totalCompare) * 100)
        : 0;

      const offer = {
        key: quantity >= 3 || frequency >= 3 ? 'three_month' : 'monthly',
        quantity,
        frequency,
        unit: meta.unit || 'month',
        planId: String(allocation.planId || ''),
        planName: allocation.planName || '',
        totalPrice,
        totalCompare,
        perMonthPrice: meta.unit.includes('month') ? totalPrice / frequency : totalPrice,
        discountPercent,
      };

      if (!offers[offer.key]) offers[offer.key] = offer;
    });

    return offers;
  } 

  function formatMoney(cents, config) {
    const amount = toNumber(cents) / 100;
    const currency = String(config.currency || 'AUD').toUpperCase();

    // Dollar-based currencies: show a simple $
    const dollarCurrencies = [
      'AUD',
      'NZD',
      'USD',
      'CAD',
      'SGD',
      'HKD'
    ];

    if (dollarCurrencies.includes(currency)) {
      return '$' + amount.toFixed(2);
    }

    // Pound
    if (currency === 'GBP') {
      return '£' + amount.toFixed(2);
    }

    // Euro
    if (currency === 'EUR') {
      return '€' + amount.toFixed(2);
    }

    // Everything else — let browser format it with currency code/symbol
    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-AU',
        {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(amount);
    } catch (error) {
      return currency + ' ' + amount.toFixed(2);
    }
  }

  function pluralUnit(unit, number) {
    const clean = String(unit || 'month').replace(/s$/, '');
    return number === 1 ? clean : clean + 's';
  }

  function dynamicDescription(offer) {
    if (!offer || offer.key === 'onetime') return '';
    return `${offer.quantity} month supply, delivered every ${offer.frequency} ${pluralUnit(offer.unit, offer.frequency)}`;
  }

  function ensureInput(form, name) {
    let input = form.querySelector(`[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    return input;
  }

  function setNativeForm(productInfo, offer) {
    const forms = Array.from(productInfo.querySelectorAll('form[action*="/cart/add"]'))
      .filter((form) => form.querySelector('[name="id"]'));

    forms.forEach((form) => {
      const planInput = ensureInput(form, 'selling_plan');
      planInput.value = offer.planId || '';
      planInput.dispatchEvent(new Event('change', { bubbles: true }));

      const quantityInputs = form.querySelectorAll('[name="quantity"]');
      if (quantityInputs.length) {
        quantityInputs.forEach((input) => {
          input.value = String(offer.quantity || 1);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } else {
        const quantityInput = ensureInput(form, 'quantity');
        quantityInput.value = String(offer.quantity || 1);
      }
    });
  }

  function priceHTML(offer, config) {
    const finalPrice = formatMoney(offer.totalPrice, config);
    const hasCompare = offer.totalCompare > offer.totalPrice + 0.5;
    const comparePrice = hasCompare ? formatMoney(offer.totalCompare, config) : '';

    return hasCompare
      ? `<span class="custom-atc-final-price">${finalPrice}</span><span class="custom-atc-compare-price" style="text-decoration:line-through;opacity:.65;margin-left:6px;">${comparePrice}</span>`
      : `<span class="custom-atc-final-price">${finalPrice}</span>`;
  }

  function updateThemePrices(productInfo, offer, config) {
    const html = priceHTML(offer, config);

    if (config.updateMainAtcPrice) {
      const button = productInfo.querySelector('[data-add-to-cart]');
      if (button) {
        const text = button.querySelector('[data-add-to-cart-text], .btn__text');
        if (text) {
          let price = text.querySelector('[data-product-price]');
          if (!price) {
            price = document.createElement('span');
            price.className = 'btn__price';
            price.setAttribute('data-product-price', '');
            text.appendChild(price);
          }
          price.innerHTML = html;
        }
      }
    }

    if (config.updateStickyPrice) {
      document
        .querySelectorAll('.cart-bar [data-product-price]')
        .forEach((sticky) => {
          sticky.innerHTML = html;
        });
    }
  }

  function hideRecharge(productInfo) {
    productInfo.dataset.customPurchaseOptionsV2Active = 'true';

    const hide = () => {
      productInfo.querySelectorAll('recharge-subscription-widget').forEach((widget) => {
        widget.style.setProperty('display', 'none', 'important');
        const wrapper = widget.closest('.product__block, .standard__app');
        if (wrapper && !wrapper.querySelector('[data-custom-purchase-options-v2]')) {
          wrapper.style.setProperty('display', 'none', 'important');
        }
      });
    };

    hide();

    const observer = new MutationObserver(hide);
    observer.observe(productInfo, { childList: true, subtree: true });
  }

  function initWidget(element) {
    if (!element || element.dataset.cpoInitialized === 'true') return;
    element.dataset.cpoInitialized = 'true';

    const config = parseJSON(element, '[data-cpo-config]');
    const productData = parseJSON(element, '[data-cpo-product]');
    const productInfo = element.closest('product-info') || element.closest('[data-product-id]') || document.querySelector('product-info');

    if (!productInfo) {
      console.warn('RHYA purchase options: product-info element was not found.');
      return;
    }

    if (config.hideRechargeWidget) hideRecharge(productInfo);

    let selectedKey = config.defaultOffer || 'three_month';
    let currentVariantId = String(
      productInfo.querySelector('form[action*="/cart/add"] [name="id"]')?.value ||
      productData.initialVariantId ||
      ''
    );

    const cards = {};
    element.querySelectorAll('[data-cpo-offer]').forEach((card) => {
      cards[card.dataset.cpoOffer] = card;
    });

    function currentVariant() {
      return (productData.variants || []).find((variant) => String(variant.id) === String(currentVariantId))
        || (productData.variants || [])[0];
    }

    function renderCard(key, offer) {
      const card = cards[key];
      if (!card) return;

      if (!offer) {
        card.hidden = true;
        return;
      }

      card.hidden = false;
      card.dataset.planId = offer.planId || '';
      card.dataset.quantity = String(offer.quantity || 1);

      const title = card.querySelector('[data-cpo-title]');
      const description = card.querySelector('[data-cpo-description]');
      const price = card.querySelector('[data-cpo-price]');
      const compare = card.querySelector('[data-cpo-compare]');
      const perMonth = card.querySelector('[data-cpo-per-month]');
      const saveBadge = card.querySelector('[data-cpo-save-badge]');
      const topBadge = card.querySelector('[data-cpo-top-badge]');
      const topBadgeLabel = card.querySelector('[data-cpo-top-badge-label]');
      const topBadgeSaving = card.querySelector('[data-cpo-top-badge-saving]');
      const benefits = card.querySelector('[data-cpo-benefits]');
      const giftBenefit = card.querySelector('[data-cpo-gift-benefit]');

      if (title) {
        if (key === 'three_month') title.textContent = config.threeMonthTitle || offer.planName || '3 Month Delivery';
        else if (key === 'monthly') title.textContent = config.monthlyTitle || offer.planName || '1 Month Delivery';
        else title.textContent = config.onetimeTitle || '1 Time Purchase';
      }

      if (description) {
        if (key === 'three_month') description.textContent = config.threeMonthDescription || dynamicDescription(offer);
        else if (key === 'monthly') description.textContent = config.monthlyDescription || dynamicDescription(offer);
        else description.textContent = config.onetimeDescription || '1 month supply, delivered once';
      }

      if (price) price.textContent = formatMoney(offer.totalPrice, config);

      if (compare) {
        const showCompare = offer.totalCompare > offer.totalPrice + 0.5;
        compare.textContent = showCompare ? formatMoney(offer.totalCompare, config) : '';
        compare.classList.toggle('is-visible', showCompare);
      }

      if (perMonth) {
        if (key !== 'onetime' && config.showPerMonth) {
          perMonth.textContent = `${formatMoney(offer.perMonthPrice, config)} / Month`;
        } else {
          perMonth.textContent = '';
        }
      }

      const savings = config.showDynamicSavings && offer.discountPercent > 0
        ? `${offer.discountPercent}%`
        : '';

      if (saveBadge) {
        const isSubscription = key === 'three_month' || key === 'monthly';
        saveBadge.textContent = isSubscription && savings
          ? `${config.monthlySavePrefix || 'SAVE'} ${savings}`
          : '';
      }

      if (topBadge) {
        let badgeLabel = '';

        if (key === 'three_month') {
          badgeLabel = config.bestValuePrefix || 'BEST VALUE';
        } else if (key === 'monthly') {
          badgeLabel = config.mostPopularPrefix || 'MOST POPULAR';
        }

        if (topBadgeLabel) {
          topBadgeLabel.textContent = badgeLabel;
        }

        if (topBadgeSaving) {
          topBadgeSaving.textContent = savings ? `SAVE ${savings}` : '';
        }
      }

      if (benefits) benefits.classList.toggle('is-enabled', Boolean(config.showBenefits));

      if (giftBenefit) {
        const eligible = config.showGiftBenefit && (
          config.giftEligibleOffer === 'both' ||
          config.giftEligibleOffer === key
        );
        giftBenefit.hidden = !eligible;
      }
    }

    function render() {
      const variant = currentVariant();
      if (!variant) return;

      const offers = buildOffers(variant);
      element._cpoOffers = offers;

      renderCard('three_month', offers.three_month);
      renderCard('monthly', offers.monthly);
      renderCard('onetime', offers.onetime);

      if (!offers[selectedKey]) {
        selectedKey = offers.three_month ? 'three_month' : offers.monthly ? 'monthly' : 'onetime';
      }

      selectOffer(selectedKey, false);
    }

    function selectOffer(key, focus = false) {
      const offer = element._cpoOffers?.[key];
      const card = cards[key];
      if (!offer || !card || card.hidden) return;

      selectedKey = key;

      Object.entries(cards).forEach(([cardKey, cardElement]) => {
        const selected = cardKey === key;
        cardElement.classList.toggle('is-selected', selected);
        cardElement.setAttribute('aria-checked', selected ? 'true' : 'false');

        const topBadge = cardElement.querySelector('[data-cpo-top-badge]');
        if (topBadge) {
          topBadge.hidden = !selected;
        }

        const saveBadge = cardElement.querySelector('[data-cpo-save-badge]');
        if (saveBadge) {
          saveBadge.hidden = selected;
        }
      });

      setNativeForm(productInfo, offer);
      updateThemePrices(productInfo, offer, config);

      element.dispatchEvent(new CustomEvent('rhya:purchase-option-change', {
        bubbles: true,
        detail: { offer, key, variantId: currentVariantId }
      }));

      if (focus) card.focus({ preventScroll: true });
    }

    Object.entries(cards).forEach(([key, card]) => {
      card.addEventListener('click', () => selectOffer(key));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectOffer(key, true);
        }
      });
    });

    function checkVariant() {
      const value = String(
        productInfo.querySelector('form[action*="/cart/add"] [name="id"]')?.value || ''
      );

      if (value && value !== currentVariantId) {
        currentVariantId = value;
        render();
      }
    }

    productInfo.addEventListener('change', (event) => {
      if (event.target.matches('[name="id"], [data-option-value], select')) {
        window.setTimeout(checkVariant, 0);
      }
    });

    document.addEventListener('variant:change', checkVariant);
    document.addEventListener('product:variant-change', checkVariant);
    document.addEventListener('theme:variant:change', checkVariant);

    render();
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) initWidget(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
